import cn from 'classnames';
import emoji from 'emoji-dictionary';
import Image from 'next/future/image';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import htmlParser from 'react-markdown/plugins/html-parser';

import markdownStyle from '../../../css/markdown.module.css';
import ROUTES from '../../../routes';
import CodeBlock from '../../CodeBlock';
import HeadingRenderer from '../../HeadingRenderer';
import { SectionHeading } from '../../landing/sectionComponents';
import Share from '../../Share';

import styles from './post.module.css';

import { loaderProp } from '@utils/random';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function formatDate(timestamp) {
  const date = new Date(timestamp);
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  const day = date.getUTCDate();

  let suffix = 'th';
  if (day === 1 || day === 21 || day === 31) suffix = 'st';
  else if (day === 2 || day === 22) suffix = 'nd';
  else if (day === 3 || day === 23) suffix = 'rd';

  return `${month} ${day}${suffix}, ${year}`;
}

const Post = ({ post, route = ROUTES.blogPost }) => {
  const parseHtml = htmlParser({
    isValidNode: (node) => node.type !== 'script',
  });

  const emojiSupport = (text) =>
    text.value.replace(/:\w+:/gi, (name) => emoji.getUnicode(name));

  const renderers = {
    code: CodeBlock,
    heading: (props) => (
      <HeadingRenderer {...props} baseHref={route(post.slug).asPath} />
    ),
    text: emojiSupport,
  };

  return (
    <>
      <div className="-mt-16 md:mt-0"></div>
      <SectionHeading
        isH1={true}
        text={post.title}
        moreStyles={{ maxWidth: '800px', lineHeight: '1.15' }}
      />
      <div className="text-gray-500 flex items-center font-inter flex-wrap mt-4 md:mt-6 mb-4 justify-center text-sm md:text-base font-medium">
        {post.author && (
          <div className="flex items-center">
            {post.author.fields?.name || post.author.name}
            <span className="block h-[6px] w-[6px] rounded-lg bg-[#666666] mx-4"></span>
          </div>
        )}
        <div>{formatDate(post.publishDate || post.date)}</div>
      </div>
      {post.coverImage && (
        <div
          style={{ maxWidth: '800px' }}
          className="w-full mx-auto my-6 md:my-10"
        >
          <figure className={styles.cover_image_container}>
            <Image
              src={post.coverImage.fields.file.url}
              className={styles.cover_image}
              width="800"
              height="450"
              alt="Post Cover Image"
              loader={loaderProp}
            />
          </figure>
        </div>
      )}
      <div className="max-w-2xl w-full">
        <ReactMarkdown
          className={cn(markdownStyle.popsql__markdown, styles.postMarkdown)}
          source={post.body_markdown || post.body}
          escapeHtml={false}
          renderers={renderers}
          astPlugins={[parseHtml]}
        />
        <Share articleName={post.title} feedback={false} />
      </div>
    </>
  );
};

export default Post;
